import {useEffect, useMemo, useState} from 'react';
import {useFormContext} from "react-hook-form";
import {PARTIAL_FORM, PROSPECT_NAMES} from "v4/data/apiRoutes";
import useFetch from "v4/hooks/useFetch";
import {generateUrl} from "v4/services/api.service";
import { addBreadcrumb, captureException } from "@sentry/react";
import {trackQuoteAttributes} from "v4/services/debug/quoteAttributesSentry";

export default function useQuotesProductsTableAttributesPopup({index, productId}) {
    const [{data}, fetchPartialForm] = useFetch();
    const {getValues, setValue} = useFormContext();
    const [fields, setFields] = useState(null);

    const productAttributes = useMemo(() => getValues(`quoteLines.${index}.quoteLineInfo.quoteLineInfoProductAttributes`), [getValues, index]);
    const product = useMemo(() => getValues(`quoteLines.${index}.quoteLineInfo.product`), [getValues, index]);

    useEffect(() => {
        if (productId && !productAttributes?.length) {
            fetchPartialForm({
                url: generateUrl(PARTIAL_FORM, {formType: 'QuoteLine', productId: productId}),
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, productAttributes]);

    useEffect(() => {
        addBreadcrumb({
            category: 'pv-1789',
            level: 'info',
            message: 'popup:open',
            data: {
                index,
                productId,
                productAttributesLen: productAttributes?.length ?? null,
                productAttributesPaTypeof: Array.isArray(productAttributes)
                    ? Array.from(new Set(productAttributes.map(a => {
                        const pa = a?.productAttribute;
                        return pa === undefined ? 'undefined' : (pa === null ? 'null' : typeof pa);
                    })))
                    : null,
                dataPresent: !!data,
            },
        });

        const definitionIndex = buildDefinitionIndex(product, data);
        const resolveDefinition = (attribute) => definitionIndex.get(attribute?.productAttribute?.id) ?? attribute?.productAttribute ?? null;

        let attributes;
        if (productAttributes?.length > 0) {
            attributes = productAttributes;
        } else {
            attributes = getPartialFormAttributes(data);
            if (attributes) {
                attributes.forEach(attribute => {
                    const type = getRealType(resolveDefinition(attribute)?.type);
                    if (type === 'checkbox') {
                        attribute.value = attribute.value === 'true';
                    } else {
                        attribute.value = attribute.value ?? '';
                    }
                });
                trackQuoteAttributes('popup:setValue', [{quoteLineInfo: {quoteLineInfoProductAttributes: attributes}}], {index, productId, source: 'partial-form'});
                setValue(`quoteLines.${index}.quoteLineInfo.quoteLineInfoProductAttributes`, attributes);
            }
        }

        if (!attributes) return;

        try {
            const _fields = attributes.map((attribute, attributeIndex) => {
                const definition = resolveDefinition(attribute);

                if (!definition) {
                    addBreadcrumb({
                        category: 'pv-1789',
                        level: 'warning',
                        message: 'popup:attr-unresolved',
                        data: {index, attributeIndex, attributeKeys: Object.keys(attribute ?? {})},
                    });
                    return null;
                }

                return {
                    ...attribute,
                    ...definition,
                    attr: getAttr(definition.type),
                    type: getRealType(definition.type),
                    name: `quoteLines.${index}.quoteLineInfo.quoteLineInfoProductAttributes.${attributeIndex}.value`,
                };
            }).filter(Boolean);

            setFields(_fields);
        } catch (e) {
            captureException(e, {
                tags: {
                    issueLink: 'https://sfimultimedia.atlassian.net/browse/PV-1789',
                },
                extra: {
                    data,
                    attributes,
                    productAttributes,
                    index,
                    productId,
                    source: (productAttributes?.length > 0) ? 'form(productAttributes)' : 'partial-form(data)',
                    attributesPaTypeof: Array.isArray(attributes)
                        ? Array.from(new Set(attributes.map(a => {
                            const pa = a?.productAttribute;
                            return pa === undefined ? 'undefined' : (pa === null ? 'null' : typeof pa);
                        })))
                        : null,
                    firstAttributeKeys: Array.isArray(attributes) ? Object.keys(attributes[0] ?? {}) : null,
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return [fields];
}

// Le partial-form QuoteLine expose la collection d'attributs sous l'alias PLAT
// `quoteLineInfo_quoteLineInfoProductAttributes` (même convention que `allInputs`).
function getPartialFormAttributes(data) {
    const results = data?.['hydra:member']?.results;
    return results?.quoteLineInfo_quoteLineInfoProductAttributes?.value;
}

function buildDefinitionIndex(product, data) {
    const index = new Map();

    const configDefinitions = product?.productConfiguration?.productAttributes;
    if (Array.isArray(configDefinitions)) {
        configDefinitions.forEach(definition => {
            if (definition?.id) index.set(definition.id, definition);
        });
    }

    const partialFormEntries = getPartialFormAttributes(data);
    if (Array.isArray(partialFormEntries)) {
        partialFormEntries.forEach(({productAttribute}) => {
            if (productAttribute?.id && !index.has(productAttribute.id)) index.set(productAttribute.id, productAttribute);
        });
    }

    return index;
}

function getRealType(type) {
    switch (type) {
        case 'integer':
            return 'number';
        case 'boolean':
            return 'checkbox';
        case 'prospect':
        default:
            return 'text';
    }
}

function getAttr(type) {
    switch (type) {
        case 'prospect':
            return {
                'data-autocomplete': generateUrl(PROSPECT_NAMES, {}, false),
                'data-field': 'fullname',
                'data-prevent-unknown': true,
            };
        default:
            return {};
    }
}
